import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useNavigate } from 'react-router';
import type { FormErrors } from '@jwp/ott-common/types/form';
import type { Offer, ChooseOfferFormData } from '@jwp/ott-common/types/checkout';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { getCleengOfferPrice } from '@jwp/ott-common/src/utils/offers';
import { testId } from '@jwp/ott-common/src/utils/common';
import ChevronRight from '@jwp/ott-theme/assets/icons/chevron_right.svg?react';
import ChevronLeft from '@jwp/ott-theme/assets/icons/chevron_left.svg?react';
import Button from '../Button/Button';
import FormFeedback from '../FormFeedback/FormFeedback';
import DialogBackButton from '../DialogBackButton/DialogBackButton';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import Icon from '../Icon/Icon';
import IconButton from '../IconButton/IconButton';

import styles from './ChooseOfferForm.module.scss';

type OfferBoxProps = {
  offer: Offer;
  selected: boolean;
} & Pick<Props, 'onChange'>;

const OfferBox: React.FC<any> = ({ offer, selected, onChange }: any) => {
  const { t } = useTranslation('account');

  const renderOption = ({ title, periodString, secondBenefit }: { title: string; periodString?: string; secondBenefit?: string }) => (
    <div className={styles.offer} role="option">
      <input
        className={styles.radio}
        onChange={onChange}
        type="radio"
        name={'selectedOfferId'}
        value={offer.id}
        id={offer.id}
        checked={selected}
        data-testid={testId(offer.id)}
      />
      <label className={styles.label} htmlFor={offer.id}>
        <h2 className={styles.offerTitle}>{title}</h2>
        <div className={styles.offerPrice}>{getCleengOfferPrice(offer)}</div>
        <div className={styles.offerTime}>
          {!!periodString && (
            <small>
              {t('choose_offer.per')} {periodString}
            </small>
          )}
        </div>
        {/* <ul className={styles.offerBenefits}>
          {offer.freeDays || offer.freePeriods ? (
            <li>
              <Icon icon={CheckRed} />
            </li>
          ) : null}

          {!!secondBenefit && (
            <li>
              <Icon icon={CheckRed} /> {secondBenefit}
            </li>
          )}
          <li>
            <Icon icon={CheckRed} /> <span>{t('choose_offer.benefits.watch_on_all_devices')}</span>
          </li>
        </ul> */}
        <div className={styles.fill} />
      </label>
    </div>
  );

  return renderOption({ title: offer.title, periodString: offer.periodString, secondBenefit: offer.secondBenefit });
};

type Props = {
  values: ChooseOfferFormData;
  errors: FormErrors<ChooseOfferFormData>;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  onBackButtonClickHandler?: () => void;
  offers: any;
  showOfferTypeSwitch: boolean;
  submitting: boolean;
};

const ChooseOfferCleengForm: React.FC<Props> = ({ values, errors, submitting, offers, showOfferTypeSwitch, onChange, onBackButtonClickHandler }: Props) => {
  const siteName = useConfigStore((s) => s.config.siteName);
  const { t } = useTranslation('account');
  const navigate = useNavigate();
  const { selectedOfferType, selectedOfferId } = values;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setIsAtStart(scrollLeft === 0);
      setIsAtEnd(scrollLeft + clientWidth > scrollWidth - 30);
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const handleNext = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: 200,
        behavior: 'smooth',
      });
    }
  };

  const handlePrevious = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: -200,
        behavior: 'smooth',
      });
    }
  };
  const isSingleOrTwoOffers = offers.length === 1 || offers.length === 2;

  return (
    <>
      <form data-testid={testId('choose-offer-form')} noValidate>
        {onBackButtonClickHandler ? <DialogBackButton onClick={onBackButtonClickHandler} /> : null}

        <h1 className={styles.title}>{t('choose_offer.title')}</h1>
        {/* <p className={styles.subtitle}>{t('choose_offer.watch_this_on_platform', { siteName })}</p> */}
        {errors.form ? <FormFeedback variant="error">{errors.form}</FormFeedback> : null}
        {showOfferTypeSwitch && (
          <div className={styles.offerGroupSwitch}>
            <input
              className={styles.radio}
              onChange={onChange}
              type="radio"
              name="selectedOfferType"
              id="svod"
              value="svod"
              checked={selectedOfferType === 'svod'}
            />
            <label className={classNames(styles.label, styles.offerGroupLabel)} htmlFor="svod">
              {t('choose_offer.subscription')}
            </label>
            <input
              className={styles.radio}
              onChange={onChange}
              type="radio"
              name="selectedOfferType"
              id="tvod"
              value="tvod"
              checked={selectedOfferType === 'tvod'}
            />
            <label className={classNames(styles.label, styles.offerGroupLabel)} htmlFor="tvod">
              {t('choose_offer.one_time_only')}
            </label>
          </div>
        )}
        <div className={styles.offers} ref={containerRef}>
          {!offers.length ? (
            <p>{t('choose_offer.no_pricing_available')}</p>
          ) : (
            offers
              .filter((e: any) => e.active && e.tags.includes('web'))
              .map((offer: any) => <OfferBox key={offer.id} offer={offer} selected={selectedOfferId === offer.id} onChange={onChange} />)
          )}
          {!isSingleOrTwoOffers && !isAtStart && (
            <div className={styles.previousButton} onClick={handlePrevious}>
              <IconButton className={styles.clearButton} aria-label={t('search_bar.clear_search_label')}>
                <Icon icon={ChevronLeft} />
              </IconButton>
            </div>
          )}
          {!isSingleOrTwoOffers && !isAtEnd && (
            <div className={styles.nextButton} onClick={handleNext}>
              <IconButton className={styles.clearButton} aria-label={t('search_bar.clear_search_label')}>
                <Icon icon={ChevronRight} />
              </IconButton>
            </div>
          )}
        </div>
        {submitting && <LoadingOverlay transparentBackground inline />}
        <center>
          <Button
            onClick={() => navigate(`/payment-checkout?offerId=${selectedOfferId}`)}
            // onClick={() => window.location.href =`${PATH_USER_CHECKOUT}&offerId=${selectedOfferId}`}
            label={t('choose_offer.continue')}
            disabled={submitting || !offers.length || !selectedOfferId}
            variant="contained"
            color="primary"
            type="button"
            halfwith={true}
          />
        </center>
      </form>
    </>
  );
};
export default ChooseOfferCleengForm;
